import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useSharePoint } from '../../../contexts/SharePointContext';
import { documentService } from '../../../services/documentService';

const SharePointConfigModal = ({ isOpen, onClose, onConfigured }) => {
  const {
    isConfigured,
    selectedSite,
    selectedDrive,
    availableSites,
    availableDrives,
    loading,
    error,
    microsoftToken,
    selectSite,
    selectDrive,
    authenticateWithMicrosoft,
    loadSharePointSites,
    resetConfiguration
  } = useSharePoint();

  const [localSite, setLocalSite] = useState(null);
  const [localDrive, setLocalDrive] = useState(null);
  const [localLoading, setLocalLoading] = useState(false);
  const [localError, setLocalError] = useState(null);

  // Auto-load sites when modal opens
  useEffect(() => {
    if (isOpen && microsoftToken && availableSites?.length === 0 && !loading) {
      console.log('[CONFIG_MODAL] Modal opened, loading SharePoint sites...');
      loadSharePointSites();
    }
  }, [isOpen, microsoftToken, availableSites, loading, loadSharePointSites]);

  useEffect(() => {
    if (selectedSite) setLocalSite(selectedSite);
    if (selectedDrive) setLocalDrive(selectedDrive);
  }, [selectedSite, selectedDrive]);

  const handleSiteChange = async (e) => {
    const siteId = e?.target?.value;
    const site = availableSites?.find(s => s?.id === siteId);
    if (site) {
      setLocalSite(site);
      await selectSite(site);
    }
  };

  const handleDriveChange = (e) => {
    const driveId = e?.target?.value;
    const drive = availableDrives?.find(d => d?.id === driveId);
    if (drive) {
      setLocalDrive(drive);
      selectDrive(drive);
    }
  };

  const handleSave = async () => {
    try {
      setLocalLoading(true);
      setLocalError(null);

      // Validate SharePoint configuration
      if (!selectedSite || !selectedDrive) {
        setLocalError('Please select both a SharePoint site and document library');
        setLocalLoading(false);
        return;
      }

      // Apply SharePoint configuration
      await selectSite(selectedSite);
      await selectDrive(selectedDrive);
      
      console.log('[CONFIG] SharePoint configuration applied:', {
        site: selectedSite?.displayName,
        drive: selectedDrive?.name
      });
      
      // Notify parent with full configuration
      onConfigured?.({
        storageStrategy: 'sharepoint',
        site: selectedSite,
        drive: selectedDrive
      });

      // Close modal
      onClose?.();
    } catch (err) {
      console.error('[CONFIG] Error saving configuration:', err);
      setLocalError(err?.message || 'Failed to save storage configuration');
    } finally {
      setLocalLoading(false);
    }
  };

  const handleUseSupabase = () => {
    // Set storage strategy back to Supabase
    documentService?.setStorageStrategy('supabase');
    
    if (onConfigured) {
      onConfigured({
        storageStrategy: 'supabase'
      });
    }
    
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Icon name="Cloud" size={20} className="text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Storage Configuration</h2>
              <p className="text-sm text-gray-500">Choose your document storage location</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <Icon name="X" size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {(error || localError) && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start gap-2">
              <Icon name="AlertTriangle" size={16} className="mt-0.5" />
              <span className="text-sm">{error || localError}</span>
            </div>
          )}

          {/* Storage Options */}
          <div className="space-y-4 mb-6">
            <div className="border-2 border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                    <Icon name="Cloud" size={24} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">SharePoint Online</h3>
                    <p className="text-sm text-gray-500">Microsoft 365 document storage</p>
                  </div>
                </div>
                <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                  Recommended
                </span>
              </div>

              {!microsoftToken ? (
                <Button
                  variant="default"
                  iconName="Key"
                  iconPosition="left"
                  onClick={authenticateWithMicrosoft}
                  disabled={loading}
                  className="w-full"
                >
                  {loading ? 'Connecting...' : 'Connect to Microsoft'}
                </Button>
              ) : (
                <div className="space-y-4">
                  {/* Site Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      SharePoint Site
                    </label>
                    <select
                      value={localSite?.id || ''}
                      onChange={handleSiteChange}
                      disabled={loading}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                    >
                      <option value="">Select a site...</option>
                      {availableSites?.map(site => (
                        <option key={site?.id} value={site?.id}>
                          {site?.displayName || site?.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Drive Selection */}
                  {localSite && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Document Library
                      </label>
                      <select
                        value={localDrive?.id || ''}
                        onChange={handleDriveChange}
                        disabled={loading || !localSite}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                      >
                        <option value="">Select a library...</option>
                        {availableDrives?.map(drive => (
                          <option key={drive?.id} value={drive?.id}>
                            {drive?.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {localSite && localDrive && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-start gap-2">
                      <Icon name="CheckCircle" size={16} className="text-green-600 mt-0.5" />
                      <div className="text-sm text-green-800">
                        <p className="font-medium">Configuration Ready</p>
                        <p className="text-green-600">
                          Documents will be stored in: {localSite?.displayName || localSite?.name} → {localDrive?.name}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="border-2 border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center">
                    <Icon name="Database" size={24} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Supabase Storage</h3>
                    <p className="text-sm text-gray-500">Built-in database storage</p>
                  </div>
                </div>
              </div>
              <Button
                variant="outline"
                iconName="Database"
                iconPosition="left"
                onClick={handleUseSupabase}
                className="w-full"
              >
                Use Supabase Storage
              </Button>
            </div>
          </div>

          {/* Info Section */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <Icon name="Info" size={16} className="text-blue-600 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">About Storage Options</p>
                <ul className="list-disc list-inside space-y-1 text-blue-700">
                  <li>SharePoint: Enterprise-grade storage with Microsoft Office integration</li>
                  <li>Supabase: Fast, reliable storage with built-in database features</li>
                  <li>You can switch between storage options at any time</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-6 flex items-center justify-end gap-3">
          <Button
            variant="outline"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleSave}
            disabled={!localSite || !localDrive || loading || localLoading}
            iconName="Check"
            iconPosition="left"
          >
            Save Configuration
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SharePointConfigModal;